import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { finalize } from 'rxjs/operators';
import {
  PageBreadcrumbComponent,
  BreadcrumbItem,
} from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import {
  SessionService,
  ActiveSession,
} from '../../../services/session/session.service';
import { AuthService } from '../../../services/auth/auth.service';

@Component({
  selector: 'app-sessions',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PageBreadcrumbComponent,
    ToastModule,
    ConfirmDialogModule,
    ButtonModule,
    TooltipModule,
  ],
  templateUrl: './sessions.component.html',
})
export class SessionsComponent implements OnInit {
  private readonly sessionService = inject(SessionService);
  private readonly authService = inject(AuthService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);

  breadcrumbs: BreadcrumbItem[] = [{ label: 'Active Sessions' }];

  sessions: ActiveSession[] = [];
  loading = false;
  revokingId: string | null = null;
  revokingOthers = false;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.sessionService
      .list()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.sessions = (res || [])
            .filter((s) => !s.revoked_at)
            .sort((a, b) => {
              if (a.is_current !== b.is_current) return a.is_current ? -1 : 1;
              return (
                new Date(b.last_active_at).getTime() -
                new Date(a.last_active_at).getTime()
              );
            });
        },
        error: () => {
          this.sessions = [];
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load active sessions.',
          });
        },
      });
  }

  deviceTitle(s: ActiveSession): string {
    const browser = s.browser_name || 'Unknown browser';
    const os = s.os_name || 'Unknown OS';
    return `${browser} on ${os}`;
  }

  deviceIcon(s: ActiveSession): string {
    if (s.device_type === 'mobile') return 'pi pi-mobile';
    if (s.device_type === 'tablet') return 'pi pi-tablet';
    return 'pi pi-desktop';
  }

  confirmRevoke(session: ActiveSession): void {
    const isCurrent = session.is_current;
    this.confirmationService.confirm({
      message: isCurrent
        ? 'Revoke this session? This is the device you are using now — you will be signed out.'
        : 'Revoke this session? That device will be signed out immediately.',
      header: 'Revoke session',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Revoke',
      rejectLabel: 'Cancel',
      accept: () => this.revoke(session),
    });
  }

  revoke(session: ActiveSession): void {
    this.revokingId = session.id;
    this.sessionService
      .revoke(session.id)
      .pipe(finalize(() => (this.revokingId = null)))
      .subscribe({
        next: () => {
          if (session.is_current) {
            this.authService.handleSessionRevoked();
            this.router.navigate(['/signin'], { replaceUrl: true });
            return;
          }
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Session revoked.',
          });
          this.load();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to revoke session.',
          });
        },
      });
  }

  confirmRevokeOthers(): void {
    this.confirmationService.confirm({
      message:
        'Sign out all other sessions? Every other device will be signed out immediately.',
      header: 'Sign out other sessions',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sign out others',
      rejectLabel: 'Cancel',
      accept: () => this.revokeOthers(),
    });
  }

  revokeOthers(): void {
    this.revokingOthers = true;
    this.sessionService
      .revokeOthers()
      .pipe(finalize(() => (this.revokingOthers = false)))
      .subscribe({
        next: (res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: res?.detail || 'Other sessions signed out.',
          });
          this.load();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to sign out other sessions.',
          });
        },
      });
  }
}
